// MIRRORS the backend mapper at
// `packages/cart_and_checkout/app/controllers/concerns/checkout_log_handler.rb`
// (REASON_CODE_EXACT_MAP, REASON_CODE_PATTERNS, RAW_LOG_CODE_MAX_LENGTH,
// reason_code_for, truncate_raw_log_code).
//
// MN-6309: frontend `track("checkout_error", ...)` calls bypass the backend's
// `notify_mixpanel_checkout_error` enrichment. Without this mapper, FE-emitted
// events would show up in Mixpanel with `log_code` set but `reason_code`
// missing — analyst dashboards keyed on reason_code would see them as null.
// Keep both lists in lockstep with the Ruby source above.

export const RAW_LOG_CODE_MAX_LENGTH = 100;

// MN-8110: match the Ruby ERROR_MESSAGE_MAX_LENGTH so the FE/BE classifier
// behaves identically for long messages. Without this, a long error_message
// with a decline phrase past char 500 would classify as 'payment_declined'
// on FE but 'other' on BE (the BE truncates before pattern matching).
export const ERROR_MESSAGE_MAX_LENGTH = 500;

export const REASON_CODE_EXACT_MAP = {
  shipment_calculation_retry: "shipment_calc_failed",
  blank_shipments: "shipment_calc_failed",
  stock_unavailable: "item_unavailable"
};

// Patterns are evaluated in array order; first match wins. Order from
// most-specific to most-general so a narrow match isn't shadowed by a broader
// one earlier in the list.
export const REASON_CODE_PATTERNS = [
  [/address-(?:invalid|no-country)/i, "address_invalid"],
  [/(?:shipping|bill)-address/i, "address_invalid"],
  [/(?:po-box|not-shippable|unverified-shipping)/i, "address_invalid"],
  [/shipment-calculation/i, "shipment_calc_failed"],
  [/(?:stock-unavailable|blank-shipments|out-of-stock)/i, "item_unavailable"],
  [/cc-invalid|order-below-limit|payment-.*(?:declined|invalid|failed)/i, "payment_declined"],
  [/session-(?:expired|error)/i, "session_expired"]
];

// MN-8110: catch-all log_codes that don't carry the failure reason in the
// code itself — customer declines arrive as ce-08-complete-order-error or
// ce-ge-01-gateway-exception-error with the actual decline information in
// the error_message text. Falls back to message inspection ONLY when no
// REASON_CODE_PATTERNS regex matched.
export const GENERIC_LOG_CODES = ["ce-08-complete-order-error", "ce-ge-01-gateway-exception-error"];

// MN-8110: gateway customer-decline message signatures. Keep in lockstep with
// the Ruby DECLINE_MESSAGE_PATTERNS constant. PayPal customer declines come
// back as UNPROCESSABLE_ENTITY responses whose body contains "declined by the
// processor or bank" — so /declined/i catches them. We deliberately do NOT
// match UNPROCESSABLE_ENTITY itself because PayPal uses the same top-level
// name for non-decline state errors that must remain visible to the alarm.
export const DECLINE_MESSAGE_PATTERNS = [
  /(?:card|instrument|payment|charge|transaction)\s+(?:was|is)\s+declined/i,
  /declined\s+(?:by|due\s+to)/i,
  /insufficient\s+funds/i,
  /card\s+has\s+expired/i,
  /card\s+number\s+is\s+incorrect/i,
  /does\s+not\s+support\s+this\s+type\s+of\s+purchase/i,
  /\b2001:\s+insufficient/i,
  /repeated\s+attempts\s+too\s+frequently/i
];

const isBlank = value => value == null || value === "";

// gemini review: regex.test silently coerces non-strings to "[object Object]"
// etc. — guard on type so a future caller passing an Error / Hash here
// returns false instead of falsely matching the coerced placeholder.
// codex review: cap at ERROR_MESSAGE_MAX_LENGTH so FE/BE classification stays
// in lockstep on long messages (e.g. full HTML 500 pages, full deadlock traces).
const genericLogCodeWithDeclineMessage = (logCode, errorMessage) => {
  if (!GENERIC_LOG_CODES.includes(logCode)) return false;
  if (typeof errorMessage !== "string" || isBlank(errorMessage)) return false;
  const capped = errorMessage.slice(0, ERROR_MESSAGE_MAX_LENGTH);
  return DECLINE_MESSAGE_PATTERNS.some(pattern => pattern.test(capped));
};

export const reasonCodeFor = (logCode, errorMessage = null) => {
  if (isBlank(logCode)) return "other";
  if (Object.prototype.hasOwnProperty.call(REASON_CODE_EXACT_MAP, logCode)) {
    return REASON_CODE_EXACT_MAP[logCode];
  }
  const match = REASON_CODE_PATTERNS.find(([pattern]) => pattern.test(logCode));
  if (match) return match[1];
  if (genericLogCodeWithDeclineMessage(logCode, errorMessage)) return "payment_declined";
  return "other";
};

export const truncateRawLogCode = logCode => {
  if (isBlank(logCode)) return logCode;
  return String(logCode).slice(0, RAW_LOG_CODE_MAX_LENGTH);
};

export const enrichCheckoutErrorProperties = properties => {
  const logCode = properties?.log_code;
  const errorMessage = properties?.error_message;
  return {
    ...properties,
    reason_code: reasonCodeFor(logCode, errorMessage),
    raw_log_code: truncateRawLogCode(logCode)
  };
};
